import { useQuery } from '@apollo/client';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useGeolocation } from '@myphx-engineering/location-utils';

import { Loader } from '../../components/common/Loader';
import { ProviderSelection } from '../../components/provider-offers/ProviderSelection';
import { getFragment } from '../../graphql';
import {
    type ChainProviderInfoFragment,
    GetAnonymousPrescriberOrderDocument,
    type MailOrderProviderInfoFragment,
    type PharmacyProviderInfoFragment,
    type ProviderOffersInfoFragment,
    SearchPharmaciesDocument,
    type SearchPharmaciesQuery,
} from '../../graphql/generated/graphql';
import { useNumericSearchParam } from '../../hooks/use-numeric-search-param';
import { QueryLoader } from '../../loaders';
import { getPrimaryChainProvider } from '../../util';
import { getAbsoluteRoute } from '../../util/get-absolute-route';

export const SimplifiedProviderSelectionRoute = () => {
    const { drxId } = useParams();
    const navigate = useNavigate();
    const { geolocation } = useGeolocation();
    const distance = useNumericSearchParam('within');

    if (!drxId) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    // oxlint-disable-next-line react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md
    const { data, loading } = useQuery(GetAnonymousPrescriberOrderDocument, {
        variables: { drxId },
        skip: !drxId,
    });

    if (loading) {
        return <Loader />;
    }

    const providerId = getFragment(
        data?.anonymousPrescriberOrder?.assignedPharmacyProvider
    )?.id;

    // Redirect to order confirmation if already routed
    if (providerId) {
        navigate(
            getAbsoluteRoute('direct.drxId.confirmation', {
                drxId,
            })
        );
    }

    const handlePharmacySelect = (
        providerOffersId: string,
        providerId?: string
    ) => {
        navigate(
            getAbsoluteRoute('direct.drxId.providerId.details', {
                drxId,
                providerId: providerId || providerOffersId,
            })
        );
    };

    // Convert SearchPharmacies results to ProviderOffersInfoFragment format
    const processProviders = (
        edges: SearchPharmaciesQuery['findProviders']['edges']
    ): {
        delivery: ProviderOffersInfoFragment[];
        pickup: ProviderOffersInfoFragment[];
    } => {
        const pickup: ProviderOffersInfoFragment[] = [];
        const delivery: ProviderOffersInfoFragment[] = [];

        // Separate providers by type
        const { pharmacyProviders, chainProviders, mailOrderProviders } =
            edges.reduce<{
                pharmacyProviders: PharmacyProviderInfoFragment[];
                chainProviders: ChainProviderInfoFragment[];
                mailOrderProviders: MailOrderProviderInfoFragment[];
            }>(
                (acc, edge) => {
                    const provider = getFragment(edge.node);
                    switch (provider.__typename) {
                        case 'PharmacyProvider':
                            acc.pharmacyProviders.push(provider);
                            break;
                        case 'ChainProvider':
                            acc.chainProviders.push(provider);
                            break;
                        case 'MailOrderProvider':
                            acc.mailOrderProviders.push(provider);
                            break;
                    }
                    return acc;
                },
                {
                    pharmacyProviders: [],
                    chainProviders: [],
                    mailOrderProviders: [],
                }
            );

        const allPharmacyProviders: (
            | PharmacyProviderInfoFragment
            | ChainProviderInfoFragment
        )[] = [
            ...pharmacyProviders,
            ...chainProviders.filter((chain) => chain.pharmacies?.length > 0),
        ];

        // Convert to provider offers format
        const createProviderOffer = (
            provider:
                | PharmacyProviderInfoFragment
                | ChainProviderInfoFragment
                | MailOrderProviderInfoFragment
        ): ProviderOffersInfoFragment => {
            let providerId = provider.id;
            let primaryPharmacy: PharmacyProviderInfoFragment | undefined;
            let chainName: string | undefined;
            let pharmacies;

            if (provider.__typename === 'ChainProvider') {
                ({ chainName, pharmacies } = provider);

                primaryPharmacy = getPrimaryChainProvider(
                    provider,
                    geolocation
                );
                if (primaryPharmacy) {
                    providerId = primaryPharmacy.id;
                }
            }

            return {
                __typename: 'ProviderOffers',
                id: providerId,
                distance: 0,
                offers: [],
                medication: {},
                provider: {
                    ...provider,
                    ...(provider.__typename === 'ChainProvider' && {
                        id: provider.id,
                        chainName,
                        pharmacies,
                        ...(primaryPharmacy && {
                            latitude: primaryPharmacy.latitude,
                            longitude: primaryPharmacy.longitude,
                        }),
                    }),
                },
                prescriberOrder: { id: '1' },
                product: {},
            };
        };

        allPharmacyProviders.forEach((provider) => {
            pickup.push(createProviderOffer(provider));
        });

        mailOrderProviders.forEach((provider) => {
            delivery.push(createProviderOffer(provider));
        });

        return { pickup, delivery };
    };

    return (
        <QueryLoader
            query={SearchPharmaciesDocument}
            variables={{
                searchParams: {
                    connectionPageInput: {
                        first: 50,
                    },
                    includeInNetworkOnly: true,
                    within: {
                        radiusMiles: distance,
                        coordinates: geolocation
                            ? {
                                  latitude: geolocation.latitude,
                                  longitude: geolocation.longitude,
                              }
                            : undefined,
                    },
                },
            }}
            component={({ data }) => {
                const { pickup, delivery } = processProviders(
                    data?.findProviders?.edges ?? []
                );

                return (
                    <ProviderSelection
                        providerOffersByType={{
                            pickup,
                            delivery,
                        }}
                        onSelect={handlePharmacySelect}
                        simplified={true}
                        header={undefined}
                    />
                );
            }}
        />
    );
};
